import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Loader2, BookOpen, Sparkles } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ProductSection() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [prod, setProd] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/program/home").then((r) => setProd(r.data)).catch(() => {}); }, [user]);

  if (!prod || !prod.available) return null;

  const buy = async () => {
    if (prod.owned) { navigate("/percorso"); return; }
    if (!user) { login(); return; }
    if (!prod.lookup_key) { toast.info("Presto disponibile"); return; }
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: prod.lookup_key, origin_url: window.location.origin,
        item_type: "program", item_id: "signature",
      });
      window.location.href = r.data.checkout_url;
    } catch { toast.error("Errore checkout"); setBusy(false); }
  };

  const includes = prod.includes || [];

  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 pb-20 sm:pb-28" data-testid="home-product-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
        className="rounded-3xl bg-[#FFFFFF] border border-[rgba(198,137,158,0.35)] overflow-hidden grid lg:grid-cols-2 shadow-sm"
      >
        <div className="aspect-square lg:aspect-auto min-h-[320px] bg-[#F3E8E2] order-1">
          {prod.image_path ? <img src={mediaUrl(prod.image_path)} alt={prod.title} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center"><Sparkles className="w-12 h-12 text-[rgba(198,137,158,0.5)]" /></div>}
        </div>

        <div className="p-8 sm:p-12 flex flex-col justify-center order-2">
          <div className="eyebrow mb-4 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Il mio prodotto</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-[#3D3238]">{prod.title || "eBook + Percorso di 21 giorni"}</h2>
          {prod.description && <p className="mt-5 text-[#8C7E83] leading-relaxed">{prod.description}</p>}

          <div className="mt-6 flex flex-wrap gap-4 text-sm text-[#3D3238]">
            <span className="inline-flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#C6899E]" /> eBook</span>
            <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C6899E]" /> Percorso 21 giorni</span>
          </div>

          {includes.length > 0 && (
            <ul className="mt-6 space-y-2">
              {includes.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-[#8C7E83] text-sm">
                  <Check className="w-4 h-4 text-[#C6899E] mt-0.5 shrink-0" /> {it}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-8 flex items-center gap-5 flex-wrap">
            <button onClick={buy} disabled={busy} data-testid="product-buy-btn"
              className="group inline-flex items-center gap-2 bg-[#C6899E] text-[#FBF5F1] rounded-full px-8 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C] transition-colors disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {prod.owned ? "Apri il percorso" : (prod.button_label || "Acquista")}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            {!prod.owned && prod.price > 0 && <span className="font-serif text-3xl text-[#C6899E]">€{prod.price}</span>}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
